import downloadInvoice from "@gerrieDev/Handlers/DownloadInvoice";
import { StatementLine } from "@gerrieDev/Services/GerrieApiModels";
import { GetStatementLinesApiParameter } from "@gerrieDev/Services/StatementService";
import { getStatementLines } from "@gerrieDev/Store/Data/StatementLines/StatementLinesSelector";
import { StatementLinesState } from "@gerrieDev/Store/Data/StatementLines/StatementLinesState";
import updateLineSearchFields from "@gerrieDev/Store/Pages/StatementDetails/Handlers/UpdateLineSearchFields";
import { CustomState } from "@gerrieDev/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageByType } from "@insite/client-framework/Services/ContentService";
import { getInvoice } from "@insite/client-framework/Services/InvoiceService";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { InvoiceDetailsLineCardStyles } from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsLineCard";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    statementLinesData: StatementLinesState,
    getStatementLinesParameter: GetStatementLinesApiParameter,
    updateLineSearchFields: (parameter: GetStatementLinesApiParameter & UpdateSearchFieldsType) => void,
}

const mapStateToProps = (state: CustomState) => ({
    orderDetailsPageLink: getPageLinkByPageType(state, "OrderDetailsPage"),
    invoiceDetailsPageLink: getPageLinkByPageType(state, "InvoiceDetailsPage"),
    invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
    session: state.context.session,
});

const mapDispatchToProps = {
    downloadInvoice
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;


export interface StatementDetailsSummaryTableStyles {
    container?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    statementDateCell?: DataTableCellBaseProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    orderNumberCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    totalAmountCell?: DataTableCellBaseProps;
    downloadPdfButton?: ShareEntityButtonStyles;
    downloadPdfButtonWrapper?: InjectableCss;
}

export const statementSummaryTableStyles: StatementDetailsSummaryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    downloadPdfButton: {
        button: {
            css: css`
                margin: 15px 0;
                width: 200px;
            `,
            buttonType: "outline",
            variant: "tertiary",
        },
    },
    downloadPdfButtonWrapper: {
        css: css`
            padding-bottom: 20px;
         `,
    }
};

const styles = statementSummaryTableStyles;

const StatementSummaryTable: React.FunctionComponent<Props> = ({
    statementLinesData,
    getStatementLinesParameter,
    updateLineSearchFields,
    orderDetailsPageLink,
    invoiceDetailsPageLink,
    invoiceHistoryPageLink,
    downloadInvoice,
    session,
}: Props) => {

    if (statementLinesData.value?.statementLines.length < 1) {
        return (
            <StyledWrapper {...styles.container}>
                {translate("Statement Lines Not Found")}
            </StyledWrapper>
        );
    }


    const orderDetailsUrl = orderDetailsPageLink?.url;
    const invoiceDetailsUrl = !invoiceHistoryPageLink?.excludeFromNavigation ? invoiceDetailsPageLink?.url : '';
    const statementLines = statementLinesData.value.statementLines.map((statementLine: StatementLine) => {
        return {
            ...statementLine,
            orderDetailsLink: orderDetailsUrl ? `${orderDetailsUrl}?orderNumber=${statementLine.orderNumber}` : '',
            invoiceDetailsLink: invoiceDetailsUrl ? `${invoiceDetailsUrl}?invoiceNumber=${statementLine.invoiceNumber}` : '',
        };
    });
    
    const downloadPDFLabel = translate("Download PDF");

    const toasterContext = useContext(ToasterContext);
    const generateInvoicePdf = (invoiceNumber: string) => {
        const invoiceError = translate("Unable to retrieve invoice.");
        downloadInvoice({
            invoiceNumber,
            onUnauthenticated: () => {
                toasterContext.addToast({
                    body: `${translate("Login Required.")}`,
                    messageType: "warning",
                });
            },
            onBeginDownload: () => {
                toasterContext.addToast({
                    body: `${translate("Retrieving invoice. It will open in a new tab when completed.")}`,
                    messageType: "info",
                    timeoutLength: 5000,
                });
            },
            onError: () => {
                toasterContext.addToast({
                    body: invoiceError,
                    messageType: "warning",
                    timeoutLength: 5000,
                });
            },
        });
    };

    const generatePdf = () => {

        if (!session?.isAuthenticated) {
            toasterContext.addToast({
                body: `${translate("Login Required.")}`,
                messageType: "warning",
            });

            return;
        }

        const statementError = translate("Unable to retrieve statement.");
        const apiUrl = `${window.origin}/api/v1/GerrieStatement/pdf?statementNumber=${statementLines[0].statementNumber}`;

        toasterContext.addToast({
            body: `${translate("Retrieving statement. It will open in a new tab when completed.")}`,
            messageType: "info",
            timeoutLength: 5000,
        });

        fetch(apiUrl, {
            method: "GET",
        }).then((response: Response) => {
            if (response.status === 200) {
                response.blob().then(blob => {
                    if (blob.size > 25) {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.target = "_blank";
                        link.click();
                    } else {
                        toasterContext.addToast({
                            body: statementError,
                            messageType: "warning",
                            timeoutLength: 5000,
                        });
                    }
                });
            }
            else {
                toasterContext.addToast({
                    body: statementError,
                    messageType: "warning",
                    timeoutLength: 5000,
                });
            }
        });
    };

    const headerClick = (sortField: string) => {
        const sort = getStatementLinesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateLineSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (getStatementLinesParameter?.sort === sortField) {
            sorted = "ascending";
        } else if (getStatementLinesParameter?.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    type StatementLineRow = StatementLine & {
        orderDetailsLink?: string;
        invoiceDetailsLink?: string;
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.downloadPdfButtonWrapper}>
                <Button
                    {...styles.downloadPdfButton}
                    onClick={() => generatePdf()}
                    variant="tertiary"
                    data-test-selector="statementDetails_download"
                >
                    {downloadPDFLabel}
                </Button>
            </StyledWrapper>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        tight
                        sorted={sorted("invoiceDate")}
                        onSortClick={() => headerClick("invoiceDate")}
                    >
                        {translate("Invoice Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("invoiceNumber")}
                        onSortClick={() => headerClick("invoiceNumber")}
                    >
                        {translate("Invoice Number")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("orderNumber")}
                        onSortClick={() => headerClick("orderNumber")}
                    >
                        {translate("Order Number")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("purchaseOrderNumber")}
                        onSortClick={() => headerClick("purchaseOrderNumber")}
                    >
                        {translate("Purchase Order Number")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("charges")}
                        onSortClick={() => headerClick("charges")}
                    >
                        {translate("Charges")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {statementLines.map((statementLine: StatementLineRow) => (
                        <DataTableRow
                            key={statementLine.invoiceNumber}
                            {...styles.dataTableRow}
                            data-test-selector="statement_statementLine"
                        >
                            <DataTableCell
                                {...styles.statementDateCell}
                                data-test-selector="statement_statementLine_date"
                            >
                                {statementLine.invoiceDate && (
                                    <LocalizedDateTime
                                        dateTime={statementLine.invoiceDate}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                )}
                            </DataTableCell>
                            <DataTableCell
                                {...styles.invoiceNumberCell}
                                data-test-selector="statement_statementLine_number"
                            >
                                {statementLine.invoiceDetailsLink ? (
                                    <StyledWrapper css={css`display: flex; flex-direction: row;`}>
                                        <Link href={statementLine.invoiceDetailsLink} target="_blank">
                                            {statementLine.invoiceNumber}
                                        </Link>|
                                        <Link css={css`font-weight: bold;`} onClick={() => {
                                            generateInvoicePdf(statementLine.invoiceNumber);
                                        }}>
                                            {translate("PDF")}
                                        </Link>
                                    </StyledWrapper>
                                ) : (
                                    <Typography>{statementLine.invoiceNumber}</Typography>
                                )}
                            </DataTableCell>
                            <DataTableCell {...styles.orderNumberCell}>
                                {statementLine.orderDetailsLink ? (
                                    <Link href={statementLine.orderDetailsLink} target="_blank">
                                        {statementLine.orderNumber}
                                    </Link>
                                ) : (
                                    <Typography>{statementLine.orderNumber}</Typography>
                                )}
                            </DataTableCell>

                            <DataTableCell {...styles.poNumberCell}>
                                {statementLine.poNumber && (
                                    <Typography>{statementLine.poNumber}</Typography>
                                )}
                            </DataTableCell>

                            <DataTableCell {...styles.totalAmountCell}>
                                {statementLine.totalAmountDisplay && (
                                    <Typography>{statementLine.totalAmountDisplay}</Typography>
                                )}
                            </DataTableCell>

                        </DataTableRow>
                    ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(StatementSummaryTable);
